import { useEffect, useMemo, useState } from 'react';
import { useUser, useClerk } from '@clerk/clerk-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LoadingContainer from '@components/LoadingContainer';
import { Button, Page } from '@nearst/ui';
import Sidebar from '@components/Sidebar';
import OnboardingNavigation from '@components/OnboardingNavigation';
import { ChatSidebar } from '@components/ChatSidebar';
import { MobileChatFab } from '@components/ChatSidebar/MobileChatFab';
import { BusinessDetails } from './components/BusinessDetails';
import { POSConnection } from './components/POSConnection';
import { GoogleBusinessProfile } from './components/GoogleBusinessProfile';
import { useBusinessDetails } from './hooks/useBusinessDetails';
import { useGoogleBusinessProfile } from './hooks/useGoogleBusinessProfile';
import { useShopInitialisation } from './hooks/useShopInitialisation';
import { useGBPConnectionPolling } from './hooks/useGBPConnectionPolling';
import { useHotjar } from './hooks/useHotjar';
import { ConnectionProvider } from './context/ConnectionContext';
import { ChatProvider } from '@services/ChatProvider';
import { switchToOrganization } from '@services/auth';
import shopApi from '@services/shop';
import { getLocalFlags } from '@services/featureFlags/useFlags';
import styles from './UnifyOnboarding.module.scss';
import useClerkLoginState from '@utils/useClerkLoginState';

const VALID_PLANS = ['unify-1', 'unify-2', 'unify-3', 'unify-4'];
const DEFAULT_PLAN = 'unify-1';

export const UnifyOnboarding = () => {
	useClerkLoginState();
	useHotjar();
	const { user, isLoaded, isSignedIn } = useUser();

	const { signOut } = useClerk();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const [orgRestored, setOrgRestored] = useState(false);

	// Extract and validate plan from URL parameter
	const plan = useMemo(() => {
		const planParam = searchParams.get('plan');
		return VALID_PLANS.includes(planParam) ? planParam : DEFAULT_PLAN;
	}, [searchParams]);

	// Restore organisation context on page load if user has an org but no active org
	useEffect(() => {
		const restoreOrgContext = async () => {
			if (!isLoaded || orgRestored) return;
			// If no user, mark as restored so the page can continue
			if (!user) {
				setOrgRestored(true);
				return;
			}

			const orgs = user.organizationMemberships || [];
			if (orgs.length === 0) {
				setOrgRestored(true);
				return;
			}

			// Check if we already have an active org in the JWT
			const token = await window.Clerk.session?.getToken();
			if (token) {
				const payload = JSON.parse(atob(token.split('.')[1]));
				if (payload.org_slug) {
					setOrgRestored(true);
					return; // Already have active org
				}
			}

			// User has orgs but no active org - set the first one as active
			const firstOrg = orgs[0].organization;
			try {
				await switchToOrganization(firstOrg.id);
				setOrgRestored(true);
			} catch (err) {
				console.error('Failed to switch to organization:', err);
			}
		};

		restoreOrgContext();
	}, [isLoaded, user, orgRestored]);

	// Business Details
	const businessDetails = useBusinessDetails(user, plan);

	// Current shop state - only initialize after org context is restored
	const { currentShop, setCurrentShop, initializing } = useShopInitialisation(
		isLoaded && orgRestored,
		navigate,
		businessDetails.setBusinessDetailsCompleted,
		businessDetails.setAddressDetails,
		businessDetails.setFormData
	);

	// Function to refresh shop data
	const refreshShopData = async () => {
		if (!currentShop) return;
		try {
			const shops = await shopApi.getMyShops();
			const updatedShop = shops.find((s) => s.id === currentShop.id);
			if (updatedShop) {
				setCurrentShop(updatedShop);
			}
		} catch (err) {
			// Silently handle error
		}
	};

	// POS Connection: Track completion status (state managed by ConnectionContext)
	const posConnection = useMemo(() => {
		const stockSource = currentShop?.inventorySources?.find((source) => source.kind === 'stock') || currentShop?.stockInventorySource;
		return {
			posConnectionCompleted: !!stockSource?.pointOfSale
		};
	}, [currentShop]);

	// Track POS connection state for chatbot context
	const [posConnectionState, setPosConnectionState] = useState(null);

	// Google Business Profile - enabled after business details
	const gbp = useGoogleBusinessProfile(businessDetails.businessDetailsCompleted, currentShop);

	// GBP connection polling
	useGBPConnectionPolling(gbp.pollingForGBPConnection, currentShop, refreshShopData, gbp.setPollingForGBPConnection, gbp.setGbpError);

	// Handler for completing onboarding manually
	const handleCompleteOnboarding = async () => {
		if (!user || user.unsafeMetadata?.onboardingComplete) return;

		try {
			await user.update({
				unsafeMetadata: {
					...user.unsafeMetadata,
					onboardingComplete: true
				}
			});
			navigate('/home');
		} catch (err) {
			console.error('Failed to update onboarding status:', err);
			// Still redirect even if metadata update fails
			navigate('/home');
		}
	};

	// Populate form data from user and handle OAuth callbacks
	useEffect(() => {
		if (!isLoaded) return;

		// If user is signed in and has completed onboarding, redirect to home
		if (user && user.unsafeMetadata?.onboardingComplete) {
			navigate('/home');
			return;
		}

		// Check if returning from OAuth - use specific callback parameters
		const gbpCallback = searchParams.get('gbp_callback');

		// Handle return from GBP OAuth
		if (gbpCallback === 'true') {
			// Start polling for GBP connection
			gbp.setPollingForGBPConnection(true);
			gbp.setGbpLoading(false);

			// Clean URL
			window.history.replaceState({}, '', '/unify-onboarding');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoaded, user, isSignedIn, navigate, searchParams]);

	const handleBusinessDetailsSubmit = async () => {
		try {
			const shop = await businessDetails.handleGetStarted();
			if (shop) {
				// Set shop immediately to prevent race condition
				// The create-shop response includes legacyId needed for GBP connection
				setCurrentShop(shop);

				// Optionally enrich with supplemental data in the background
				try {
					const shops = await shopApi.getMyShops();
					const fullShop = shops.find((s) => s.id === shop.id);
					// Only update if enriched data includes legacyId to avoid eventual consistency issues
					if (fullShop?.legacyId) {
						setCurrentShop(fullShop);
					}
				} catch (err) {
					// Shop already set with valid data, continue
				}
			}
			businessDetails.setBusinessDetailsCompleted(true);
		} catch (err) {
			// Error already handled in the hook
		}
	};

	const handleSignOut = async () => {
		await signOut();
		// Redirect to login page after sign-out
		navigate('/login');
	};

	const handleSignIn = () => {
		navigate('/login');
	};

	if (initializing) {
		return <LoadingContainer />;
	}

	const error = businessDetails.error;
	const warning = null;
	const showChat = getLocalFlags().includes('chat');

	// Build onboarding progress object for ChatProvider
	const onboardingProgress = {
		businessDetailsCompleted: businessDetails.businessDetailsCompleted,
		gbpCompleted: gbp.gbpCompleted,
		posConnectionCompleted: posConnection.posConnectionCompleted
	};

	return (
		<ChatProvider
			mode="onboarding"
			currentShop={currentShop}
			onboardingProgress={onboardingProgress}
			connectionState={posConnectionState}
			userId={user?.id}
		>
			<div className={styles.onboardingWrapper}>
				<Sidebar.Layout>
					{/* Left Navigation Sidebar */}
					<OnboardingNavigation
						shop={currentShop}
						isSignedIn={isSignedIn}
						onSignOut={handleSignOut}
						gbpCompleted={gbp.gbpCompleted}
					/>

					{/* Chat Sidebar - between nav and main content */}
					{showChat && <ChatSidebar />}

					{/* Main Content */}
					<Page.Main className={showChat && styles.chatVisible}>
						<div className={styles.container}>
							<div id="google-places-autocomplete-helper" />
							<div className={styles.header}>
								<div>
									<h1 className={styles.title}>Let&apos;s get started</h1>
									<p className={styles.subtitle}>Get your shop set up on Unify by NearSt in 3 simple steps.</p>
								</div>
								{!isSignedIn && (
									<div className={styles.signInContainer}>
										<span className={styles.signInPrompt}>Already signed up?</span>
										<button className={styles.signOutButton} onClick={handleSignIn}>
											Sign in
										</button>
									</div>
								)}
							</div>

							{error && (
								<div className={styles.error} data-cy="error-message">
									{error}
								</div>
							)}

							{warning && !error && <div className={styles.warning}>{warning}</div>}

							<div className={styles.stepsContainer}>
								<BusinessDetails
									isCompleted={businessDetails.businessDetailsCompleted}
									isExpanded={businessDetails.businessDetailsExpanded}
									isEditing={businessDetails.isEditing}
									selectedPlace={businessDetails.selectedPlace}
									addressDetails={businessDetails.addressDetails}
									formData={businessDetails.formData}
									loading={businessDetails.loading}
									validationErrors={businessDetails.validationErrors}
									onPlaceSelect={businessDetails.handlePlaceSelect}
									onFormChange={businessDetails.handleFormChange}
									onFieldBlur={businessDetails.handleFieldBlur}
									onSubmit={handleBusinessDetailsSubmit}
									onEdit={businessDetails.handleEdit}
									onCancel={businessDetails.handleCancel}
									onChangeStore={businessDetails.handleChangeStore}
									isSignedIn={businessDetails.isSignedIn}
									showPassword={businessDetails.showPassword}
									authError={businessDetails.authError}
									onEmailBlur={businessDetails.handleEmailBlur}
									isAuthenticating={businessDetails.isAuthenticating}
								/>

								<GoogleBusinessProfile
									isActive={isSignedIn && businessDetails.businessDetailsCompleted && !gbp.gbpCompleted}
									isCompleted={gbp.gbpCompleted}
									businessDetailsCompleted={isSignedIn && businessDetails.businessDetailsCompleted}
									gbpLoading={gbp.gbpLoading}
									pollingForGBPConnection={gbp.pollingForGBPConnection}
									gbpError={gbp.gbpError}
									currentShop={currentShop}
									onConnect={gbp.handleConnectGBP}
								/>

								<ConnectionProvider
									businessDetailsCompleted={isSignedIn && businessDetails.businessDetailsCompleted && gbp.gbpCompleted}
									currentShop={currentShop}
									onShopRefresh={refreshShopData}
									onConnectionStateChange={setPosConnectionState}
								>
									<POSConnection
										isActive={
											isSignedIn &&
											businessDetails.businessDetailsCompleted &&
											gbp.gbpCompleted &&
											!posConnection.posConnectionCompleted
										}
										isCompleted={posConnection.posConnectionCompleted}
										businessDetailsCompleted={
											isSignedIn && businessDetails.businessDetailsCompleted && gbp.gbpCompleted
										}
									/>
								</ConnectionProvider>

								{businessDetails.businessDetailsCompleted && posConnection.posConnectionCompleted && gbp.gbpCompleted && (
									<Button primary onClick={handleCompleteOnboarding} className={styles.completeButton}>
										Complete Unify onboarding
									</Button>
								)}
							</div>
						</div>
					</Page.Main>
				</Sidebar.Layout>

				{/* Mobile Chat FAB + Overlay */}
				{showChat && <MobileChatFab />}
			</div>
		</ChatProvider>
	);
};
